import React, {Fragment, forwardRef, memo, useEffect, useRef, useState} from 'react';
import modClasses from '@mapsight/ui/src/js/helpers/mod-classes';
import {useSelector} from 'react-redux';
import {viewSelector} from '@mapsight/ui/src/js/store/selectors';
import {VIEW_DESKTOP, VIEW_FULLSCREEN} from '@mapsight/ui/src/js/config/constants/app';

import Menu from "./menu";
import MenuButton from "./menu-button";
import LangSwitcher from './lang-switcher';

export default memo(
	/** @param {{
	 *   forceShowScrollable: boolean,
	 *   pageHeadRef: React.MutableRefObject<HTMLElement | undefined>,
	 * }} props */
	function Header({forceShowScrollable, pageHeadRef}) {
		const headerRef = useRef();
		const langRef = useRef();
		const otherLangRef = useRef();

		const [showScrollableFromScroll, setShowScrollableFromScroll] = useState(false);
		const showScrollable = forceShowScrollable || showScrollableFromScroll;

		useEffect(
			() => {
				if(!headerRef.current || !pageHeadRef.current) {
					return undefined;
				}

				const onScroll = () => {
					setShowScrollableFromScroll(
						headerRef.current.offsetHeight < -headerRef.current?.getBoundingClientRect().top
					);
				};

				// we register both as we know only one of them will fire as only one of them will scroll
				document.addEventListener('scroll', onScroll, {passive: true});
				pageHeadRef.current.parentElement.addEventListener('scroll', onScroll, {passive: true});
				return () => {
					document.removeEventListener('scroll', onScroll);
					pageHeadRef.current.parentElement.removeEventListener('scroll', onScroll);
				};
			},
			[forceShowScrollable, headerRef.current, pageHeadRef.current],
		);


		return (
			<div
				className={modClasses("stade-header", {
					"force-show-scrollable": forceShowScrollable,
				})}
			>
				{!forceShowScrollable && (
					<HeaderContent
						ref={headerRef}
						isScrolled={showScrollableFromScroll}
						forceShowScrollable={forceShowScrollable}
						langRef={langRef}
						otherLangRef={otherLangRef}
					/>
				)}

				<div
					className={modClasses('stade-header__scrollable', {
						visible: showScrollable,
					})}
				>
					<HeaderContent
						isScrollable
						isScrolled={showScrollableFromScroll}
						forceShowScrollable={forceShowScrollable}
						langRef={langRef}
						otherLangRef={otherLangRef}
					/>
				</div>

				<LangSwitcher other
							  tabRef={langRef}
							  ref={otherLangRef}
				/>
			</div>
		);
	}
);

const HeaderContent = memo(forwardRef(
	/** @param {{
	 *   isScrollable?: boolean,
	 *   isScrolled: boolean,
	 *   forceShowScrollable: boolean,
	 *   langRef: React.MutableRefObject<HTMLElement | undefined>,
	 *   otherLangRef: React.MutableRefObject<HTMLElement | undefined>,
	 * }} props */
	function HeaderContent({isScrollable = false, isScrolled, forceShowScrollable, langRef, otherLangRef}, ref) {
		const menuButtonRef = useRef();

		const view = useSelector(viewSelector);
		const openByView = view === VIEW_DESKTOP || view === VIEW_FULLSCREEN;
		const hideTabIndex = isScrollable && !forceShowScrollable;

		return (
			<Fragment>
				<div
					className={modClasses('stade-header__content', {
						scrollable: isScrollable,
						mobile: view !== 'desktop' && view !== 'fullscreen',
					})}
					ref={ref}
				>
					<div className={modClasses('stade-header__content-entry', 'logo')}>
						<div
							className={modClasses("stade-header__logo", {
								small: view !== 'desktop' && view !== 'fullscreen',
							})}
							tabIndex={hideTabIndex ? -1 : undefined}
						>
							<a
								className="stade-header__logo__link"
								href="/"
							>
								<div
									className="stade-header__logo__image"
									title="Stade Marketing & Tourismus"
								/>
							</a>
						</div>
					</div>

					<div className={modClasses('stade-header__content-entry', 'menu')}>
						{openByView && (
							<Menu
								buttonRef={menuButtonRef}
								isScrollable={isScrollable}
								isScrolled={isScrolled}
								forceShowScrollable={forceShowScrollable}
							/>
						)}
					</div>

					<div className={modClasses('stade-header__content-entry', 'controlls')}>
						<MenuButton ref={menuButtonRef} tabIndex={hideTabIndex ? -1 : undefined} />
						<LangSwitcher
							hideTabIndex={hideTabIndex}
							tabRef={otherLangRef}
							ref={hideTabIndex ? undefined : langRef}
						/>
					</div>
				</div>

				{!openByView && (
					<Menu
						buttonRef={menuButtonRef}
						isScrollable={isScrollable}
						isScrolled={isScrolled}
						forceShowScrollable={forceShowScrollable}
					/>
				)}
			</Fragment>
		);
	}
));
